import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

import InputWrapper from "../utilities/formElements/commonInput/InputWrapper";
import CommonButton from "../utilities/formElements/commonButton/CommonButton";
import { editOwnProfileValidationSchema } from "../utilities/validationSchema";
import {
  toastMessageError,
  toastMessageSuccess,
} from "../utilities/commonToastMessage";

import { editUserDetail } from "../../services/user";

import { CommonModalProps, IEditProfile } from "../../Interfaces/CommonModalInterface";
import ActionType from "../../resources/enums";
import ChangeEmail from "./ChangeEmail";

// Email is read-only HERE (a simple name/lastname form field isn't the
// right place for it - it needs its own verify-before-switch flow, not a
// plain save). Built 2026-08-07: a "Change Email" link next to the
// read-only field opens ChangeEmail.tsx, the real flow (see its own
// comment). This replaces the earlier "deferred, bigger feature" note -
// it's built now, just intentionally not folded into this simple form.
const EditProfile: React.FC<CommonModalProps> = ({ title, onClose }) => {
  const { t: translation } = useTranslation();
  const dispatch = useDispatch();
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const [loading, setLoading] = useState(false);
  const [changeEmailModel, setChangeEmailModel] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editOwnProfileValidationSchema(translation)),
  });

  const onSubmit = async (data: IEditProfile) => {
    setLoading(true);
    const result = await editUserDetail({
      firstname: data.firstname,
      lastname: data.lastname,
    });
    setLoading(false);
    if (result && result.data) {
      dispatch({
        type: ActionType.UPDATEPROFILE,
        payload: {
          firstname: data.firstname,
          lastname: data.lastname,
        },
      });
      toastMessageSuccess(translation("profile_updated"), "profileUpdated");
      onClose();
    } else {
      toastMessageError(translation("something_went_wrong"), "profileUpdateFailed");
    }
  };

  return (
    <div className="modal show-modal common-modal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">{title}</h4>
            <CommonButton
              type="button"
              className="btnC btn-close btn-close-white"
              onClick={onClose}
              data-bs-dismiss="modal"
            />
          </div>

          <div className="modal-body common-body text-primary">
            <form onSubmit={handleSubmit((data) => onSubmit(data as IEditProfile))}>
              <div className="form-group">
                <InputWrapper
                  label={translation("first_name")}
                  control={control}
                  name="firstname"
                  className="form-control"
                  placeholder={translation("first_name")}
                  error={errors?.firstname}
                  type="text"
                  value={authReducer?.authData?.firstname}
                />
              </div>
              <div className="form-group">
                <InputWrapper
                  label={translation("last_name")}
                  control={control}
                  name="lastname"
                  className="form-control"
                  placeholder={translation("last_name")}
                  error={errors?.lastname}
                  type="text"
                  value={authReducer?.authData?.lastname}
                />
              </div>
              <div className="form-group">
                <InputWrapper
                  label={translation("email")}
                  control={control}
                  name="email"
                  className="form-control"
                  type="email"
                  readOnly
                  value={authReducer?.authData?.email}
                />
                <CommonButton
                  type="button"
                  className="transparent-btn"
                  label="Change Email"
                  onClick={() => setChangeEmailModel(true)}
                />
              </div>

              <div className="text-center mt-4 mb-3">
                <CommonButton
                  type="submit"
                  className="theme-btn success-btn text-uppercase fontBold"
                  label={translation("save")}
                  loading={loading}
                  disabled={loading}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      {changeEmailModel && (
        <ChangeEmail
          title="Change Email"
          onClose={() => setChangeEmailModel(false)}
        />
      )}
    </div>
  );
};

export default EditProfile;
